import { useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { FilePdfOutlined } from "@ant-design/icons"
import { Button, Flex, Select } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { routes } from "router"
import { fetchActionQueue, resendPaymentLink, sendWaiverRequest } from "store/upcomingExperiences"
import { checkInGroup, checkOutGroup, resetGroup, resendGroupDocument } from "store/reservations"
import BookingTag from "../../BookingTag"
import PaymentTag from "../../PaymentTag"
import Toast from "./Toast"
import SendMessageButton from "../../SendMessageButton"
import CheckInBadge from "../../CheckIn/CheckInBadge/CheckInBadge"
import CheckInGuestsModal from "../../CheckIn/CheckInGuestsModal/CheckInGuestsModal"
import IncompleteDocumentsModal from "../../CheckIn/IncompleteDocumentsModal/IncompleteDocumentsModal"
import { useCheckInFlow } from "../../CheckIn/useCheckInFlow"
import { deriveStateFromGuests, buildGroupRow } from "../../CheckIn/checkInUtils"
import { PAYMENT_FILTER_OPTIONS } from "./constants"
import styles from "./ActionQueue.module.css"

const getFilteredGuests = (guests, tab, paymentFilter) => {
  switch (tab) {
    case "checkin":
      return guests.filter((guest) => guest.check_in_state === "pending")
    case "payments":
      if (paymentFilter === "all") return guests.filter((guest) => guest.payment_tag?.color !== "green")
      if (paymentFilter === "balance_due_operator_to_collect") return guests.filter((guest) => guest.collection_tag?.label === "Operator To Collect")
      if (paymentFilter === "balance_due_card_auto_collect") return guests.filter((guest) => guest.collection_tag?.label?.startsWith("Auto-collect on"))
      if (paymentFilter === "balance_due_card_auto_collect_failed") return guests.filter((guest) => guest.collection_tag?.label === "Auto-collection failed")
      if (paymentFilter === "balance_due_link_sent") return guests.filter((guest) => guest.collection_tag?.label === "Link Sent")
      return guests.filter((guest) => guest.payment_tag?.color !== "green")
    case "waivers":
      return guests.filter((guest) => guest.docs_signed < guest.docs_total)
    default:
      return guests
  }
}

const removeToast = (toasts, toastId) => toasts.filter((toast) => toast.id !== toastId)

const getToastSubtitle = (guest) => {
  const parts = []
  if ((guest.docs_signed ?? 0) < (guest.docs_total ?? 0)) parts.push("Waiver Unsigned")
  if (guest.payment_tag?.color !== "green") parts.push(guest.payment_tag?.label)
  return parts.length > 0 ? parts.join(" · ") : "Completed booking removed from queue."
}

const ActionQueue = ({ groupId }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("all")
  const [paymentFilter, setPaymentFilter] = useState("all")
  const [toasts, setToasts] = useState([])
  const [isSendingWaiverRequest, setIsSendingWaiverRequest] = useState(false)
  const [isResendingLink, setIsResendingLink] = useState(false)

  const guests = useSelector((store) => store.upcomingExperiences.actionQueues[groupId] || [])
  const isLoading = useSelector((store) => store.upcomingExperiences.actionQueueLoading[groupId])

  // Check-in is per guest-group (each guest_group is a reservation); refresh the queue after
  // so the "Check-in" tab count and state reflect the new count.
  const refreshQueue = useCallback(() => {
    if (groupId) dispatch(fetchActionQueue(groupId))
  }, [groupId, dispatch])
  const checkIn = useCheckInFlow(
    { checkIn: checkInGroup, checkOut: checkOutGroup, reset: resetGroup, resend: resendGroupDocument },
    refreshQueue
  )

  useEffect(() => {
    refreshQueue()
  }, [refreshQueue])

  const handleDismissToast = (toastId) => {
    setToasts((prev) => removeToast(prev, toastId))
  }

  const handleUndo = (toastId) => {
    setToasts((prev) => removeToast(prev, toastId))
  }

  // Both are no-body POSTs on the group; the success/error message toast is handled
  // globally by the api() helper (showSuccessNotification / default error toast).
  const handleSendWaiverRequest = () => {
    if (!groupId || isSendingWaiverRequest) return
    setIsSendingWaiverRequest(true)
    dispatch(sendWaiverRequest(groupId))
      .catch(() => {})
      .finally(() => setIsSendingWaiverRequest(false))
  }

  const handleResendPaymentLink = () => {
    if (!groupId || isResendingLink) return
    setIsResendingLink(true)
    dispatch(resendPaymentLink(groupId))
      .catch(() => {})
      .finally(() => setIsResendingLink(false))
  }

  const checkinCount = guests.filter((guest) => guest.check_in_state === "pending").length
  const paymentsCount = guests.filter((guest) => guest.payment_tag?.color !== "green").length
  const waiversCount = guests.filter((guest) => guest.docs_signed < guest.docs_total).length

  const filteredGuests = getFilteredGuests(guests, activeTab, paymentFilter)
  const isEmpty = filteredGuests.length === 0

  const waiversActionBtn = waiversCount > 0 ? (
    <Button type="primary" shape="round" onClick={handleSendWaiverRequest} loading={isSendingWaiverRequest}>
      Send Request ({waiversCount})
    </Button>
  ) : null

  const paymentsActionBtn = (() => {
    if (activeTab !== "payments") return null
    const count = filteredGuests.length
    if (count === 0) return null
    // Only two statuses expose an action: "Link Sent" resends the link, and
    // "Auto-collection failed" sends a message. All other statuses (All / Operator
    // to Collect / Auto-collect on date) show no action button.
    if (paymentFilter === "balance_due_link_sent") {
      return (
        <Button type="primary" shape="round" onClick={handleResendPaymentLink} loading={isResendingLink}>
          Resend Link ({count})
        </Button>
      )
    }
    if (paymentFilter === "balance_due_card_auto_collect_failed") {
      return (
        <SendMessageButton
          recipients={filteredGuests.map((guest) => ({ userId: guest.user_id, user: guest.user }))}
          label={`Send Message (${count})`}
          buttonProps={{ type: "primary", shape: "round" }}
        />
      )
    }
    return null
  })()

  const TABS = [
    { key: "all", label: "All" },
    { key: "checkin", label: "Check-in", count: checkinCount },
    { key: "payments", label: "Payments", count: paymentsCount },
    { key: "waivers", label: "Waivers & Docs", count: waiversCount }
  ]

  // Only show the full loading state on the initial fetch. A background refresh after a
  // check-in/out (refreshQueue) also flips isLoading, and early-returning here would unmount
  // the open modal and remount it when loading clears — the "modal closes and reopens" flicker.
  if (isLoading && guests.length === 0) {
    return <div className={styles.container}><div className={styles.header}>Action Queue</div><div className={styles.emptyState}>Loading...</div></div>
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>Action Queue</div>

      <Flex align="center" gap={8} className={styles.tabsRow}>
        <Flex align="center" gap={8}>
          {TABS.map(({ key, label, count }) => (
            <button
              key={key}
              type="button"
              className={`${styles.tabBtn} ${activeTab === key ? styles.tabBtnActive : ""}`}
              onClick={() => setActiveTab(key)}
            >
              {count !== undefined && count > 0 ? `${label} ${count}` : label}
            </button>
          ))}
          {activeTab === "waivers" && waiversActionBtn}
        </Flex>
      </Flex>

      {activeTab === "payments" && (
        <Flex vertical gap={6} className={styles.paymentFilterRow}>
          <span className={styles.filterLabel}>Filter payment issues to send reminders</span>
          <Flex align="center" gap={8}>
            <Select
              value={paymentFilter}
              onChange={setPaymentFilter}
              options={PAYMENT_FILTER_OPTIONS}
              className={styles.paymentSelect}
            />
            {paymentsActionBtn}
          </Flex>
        </Flex>
      )}

      {isEmpty ? (
        <Flex vertical className={styles.emptyState}>
          <div className={styles.emptyTitle}>All completed</div>
          <div className={styles.emptySubtitle}>(no check-ins, waivers and payments pending)</div>
        </Flex>
      ) : (
        <table className={styles.guestTable}>
          <tbody>
            {filteredGuests.map((guest) => {
              const totalGuests = guest.guest_count ?? 0
              // Reservation uuid is the check-in target; some payloads expose it as `id`.
              const reservationUuid = guest.uuid || guest.id
              const guests = buildGroupRow({
                id: reservationUuid,
                name: guest.primary_guest_name,
                guestCount: totalGuests,
                state: guest.check_in_state,
                checkedIn: guest.checked_in_count ?? 0,
                checkedOut: guest.checked_out_count ?? 0,
                docsSigned: guest.docs_signed ?? 0,
                docsTotal: guest.docs_total ?? 0,
                documents: guest.documents
              })
              const target = { id: reservationUuid, bookingId: guest.booking_id ?? reservationUuid, name: guest.primary_guest_name, guests, guestCount: totalGuests, paymentTag: guest.payment_tag, collectionTag: guest.collection_tag }
              const overrideGuests = checkIn.getGuests(target)
              const checkedInCount = checkIn.getCheckedInCount(target)
              const checkedOutCount = checkIn.getCheckedOutCount(target)
              const checkInState = deriveStateFromGuests(overrideGuests)
              const waiversComplete = (guest.docs_signed ?? 0) >= (guest.docs_total ?? 0)
              // Client detail page is keyed by the guide-client id (same as the reservation
              // detail — /guide/clients/{id}). NOT the user_id. The action-queue guest must
              // carry guide_client_id (top-level, under `user`, or as client_id) for the name
              // to link; until the backend adds it the name renders as plain text.
              const clientDetailId = guest?.guide_client_id ?? null
              return (
              <tr key={guest.id} className={styles.guestRow}>
                <td className={styles.colCheckin}>
                  <CheckInBadge
                    state={checkInState}
                    checkedInCount={checkedInCount}
                    checkedOutCount={checkedOutCount}
                    totalGuests={totalGuests}
                    onCheckIn={() => checkIn.openCheckIn(target, waiversComplete)}
                    onCheckOut={() => checkIn.openCheckOut(target)}
                    onReset={() => checkIn.reset(target)}
                  />
                </td>
                <td className={styles.colName}>
                  <Flex align="center" gap={4}>
                    {clientDetailId ? (
                      <button type="button" className={styles.guestName} onClick={() => navigate(routes.guideClientPath({ id: clientDetailId }))}>
                        {guest.primary_guest_name}
                      </button>
                    ) : (
                      <span className={styles.guestNamePlain}>{guest.primary_guest_name}</span>
                    )}
                    <span className={styles.guestCount}>({guest.guest_count} {guest.guest_count === 1 ? "guest" : "guests"})</span>
                  </Flex>
                </td>
                <td className={styles.colTags}>
                  <Flex align="center" gap={8}>
                    {(activeTab === "all" || activeTab === "checkin" || activeTab === "payments") && guest.payment_tag && (
                      <PaymentTag {...guest.payment_tag} />
                    )}
                    {(activeTab === "all" || activeTab === "payments") && guest.collection_tag && (
                      <PaymentTag {...guest.collection_tag} />
                    )}
                  </Flex>
                </td>
                <td className={styles.colWaiver}>
                  {(activeTab === "all" || activeTab === "waivers") && (guest.docs_total ?? 0) > 0 && (
                    <BookingTag color={guest.docs_signed === guest.docs_total ? "success" : "error"}>
                      <FilePdfOutlined />
                      {guest.docs_signed}/{guest.docs_total}
                    </BookingTag>
                  )}
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <IncompleteDocumentsModal
        open={!!checkIn.incompleteTarget}
        onClose={checkIn.closeIncomplete}
        onOverride={checkIn.handleOverride}
      />

      <CheckInGuestsModal
        open={!!checkIn.modalItem}
        onClose={checkIn.closeModal}
        bookingId={checkIn.modalItem?.bookingId}
        clientName={checkIn.modalItem?.name}
        paymentTag={checkIn.modalItem?.paymentTag}
        collectionTag={checkIn.modalItem?.collectionTag}
        guests={checkIn.getGuests(checkIn.modalItem)}
        isResending={checkIn.isResending}
        onToggleGuest={(guest) => checkIn.toggleGuest(checkIn.modalItem, guest)}
        onResend={(guest, document) => checkIn.resendDocument(checkIn.modalItem, guest, document)}
      />

      <Toast toasts={toasts} onUndo={handleUndo} onDismiss={handleDismissToast} />
    </div>
  )
}

ActionQueue.propTypes = {
  groupId: PropTypes.string
}

export default ActionQueue
